import { filter } from 'rxjs/operators';
import * as _ from "lodash";
import { Observable } from "rxjs";
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { OneVersusX, FixtureDetailed, MapDetails, MultiKill, Participant, PlayerExpanded, TeamStat, EntryKill } from "../fixture-details/model/FixtureDetailed";
import { AnalyzerError, ERRORS, GameStateError, UnexpectedError } from "./Errors";
import { LiveEvents } from "./model/LiveEvents";

export interface RoundState {
  teamStates: Map<number, TeamState>;
  halfNumber: number;
  winnerId: string | undefined;
  events: string[];
}

export interface TeamState {
  playerStates: Map<number, PlayerState>;
  potentialClutch: OneVersusX | undefined;
}

export interface PlayerState {
  player: LiveEvents.Player;
  kills: number;
  deaths: number;
  assists: number;
  flashAssists: number;
  entryKill: number;
  alive: boolean;
  solo: boolean;
  soloKills: number;
  planter: boolean;
  clutchPlayer: boolean; // Same as "solo" but only between Round_Start and Round_End, not overtime's end.
  clutches: OneVersusX[]; // 1v2, 1v3, 1v4, 1v5, calculated at round_end (won)
  multiKills: MultiKill[]; // Kills that contributed to the win
  entryKillClutches: EntryKill[];
}

export class LiveDataAnalyzer {
  private MP_ROUND_RESTART_DELAY: number = 7; // The time that a round is still in progress after Round_Ended
  private gameState: Map<number, Map<number, RoundState>>;

  private _fixture: FixtureDetailed;
  private fixture: ReplaySubject<FixtureDetailed>
  // TODO eventUUID + delete when voided?
  private legacy: boolean;
  private clutchSchedule: any;
  private currentOccurrenceInfo: {mapNumber: number, halfNumber: number, roundNumber: number;} // TODO use it for more than RoundEnd

  constructor(fixturedata: FixtureDetailed, liveData: Observable<LiveEvents.Event>, legacy: boolean) {
    this.legacy = legacy;
    // this._fixture = _.cloneDeep(fixturedata);
    this._fixture = {} as FixtureDetailed;
    this._fixture.status = fixturedata.status;
    this._fixture.maps = [];
    this._fixture.participants = fixturedata.participants.map(participant => { return { id: participant.id, name: participant.name, score: 0, scoreWithoutHandicap: undefined as unknown, handicap: undefined as unknown } as Participant }),
    this._fixture.winnerId = undefined!;
    this._fixture.tie = undefined!;
    this.fixture = new ReplaySubject(Infinity);

    this.gameState = new Map<number, Map<number, RoundState>>();
    liveData.subscribe(
      (event) => { 
        try {
          this.handlePayload(event);
        } catch(error) {
          if(error instanceof GameStateError) {
            this.fixture.error(error);
          } else {
            this.fixture.error(new UnexpectedError(error));
          }
          this.fixture.complete();
        }
      },
      (error) => { this.fixture.error(error) },
      () => { }
    )
  }

  private handlePayload(event: LiveEvents.Event) {

    switch (event.type) {
      case "fixture_started": {
        break;
      }
      case "fixture_ended": {
        const defraggedFixture: FixtureDetailed = _.cloneDeep(this._fixture);
        defraggedFixture.maps = defraggedFixture.maps.filter((ele) => !!ele); // defrag map
        this.fixture.next(defraggedFixture);
        this.fixture.complete();
        return;
      }
      case "occurrence": {
        let mapState: Map<number, RoundState> | undefined = this.gameState.get(event.payload.mapNumber!);
        if (!mapState) {
          this.gameState.set(event.payload.mapNumber!, new Map<number, RoundState>());
          this._fixture.maps[event.payload.mapNumber! - 1] = {
            tie: undefined as unknown,
            status: "live",
            mapName: event.payload.mapName,
            winnerId: undefined as unknown,
            teamStats: undefined as unknown,
            roundScores: this._fixture.participants.map(participant => { return { id: participant.id, roundsWon: 0 } })
          } as MapDetails
        }
        const currentMap = this._fixture.maps[event.payload.mapNumber! - 1];
        if(event.payload.mapName && currentMap.mapName !== event.payload.mapName) { // Map name changed
          currentMap.mapName = event.payload.mapName;
        }

        let roundState: RoundState | undefined = this.gameState.get(event.payload.mapNumber!)?.get(event.payload.roundNumber!);
        if (!roundState) {
          this.gameState.get(event.payload.mapNumber!)!.set(event.payload.roundNumber!, { teamStates: new Map<number, TeamState>(), halfNumber: event.payload.halfNumber!, events: [], winnerId: undefined });
          roundState = this.gameState.get(event.payload.mapNumber!)?.get(event.payload.roundNumber!)!;
        }

        const payload = event.payload;
        const uuid: string = btoa(payload.mapNumber! + payload.roundNumber! + payload.name! + payload.player?.id + payload.assister?.id + payload.victim?.id + payload.planterId + payload.defuserId);
        if (roundState.events[uuid]) {
          return; // Ignore repeated events
        } else {
          roundState.events[uuid] = uuid;
        }

        switch (event.payload.name) {
          case "map_started": {
            break;
          }
          case "round_started": {
            // if(this.clutchSchedule) { // Cancel event and do task ourselves
            //   this.clearInterval();
            //   this.handleRoundEnd();
            // }
            this.currentOccurrenceInfo = {mapNumber: event.payload.mapNumber!, halfNumber: event.payload.halfNumber!, roundNumber: event.payload.roundNumber!};
            break;
          }

          case "round_voided": {
            // this.clearInterval();
            this.gameState.get(event.payload.mapNumber!)!.delete(event.payload.roundNumber!);
            break;
          }
          case "round_ended": {
            this.gameState.get(payload.mapNumber!)!.get(payload.roundNumber!)!.winnerId = payload.winnerId!;
            this._fixture.maps[payload.mapNumber! - 1].roundScores = _(payload.participants)
              .map(score => {
                return { id: Number(score.id), roundsWon: score.roundsWon! }
              })
              .sortBy(score => score.id)
              .value()

            if(this.legacy) {
              this.handlePlayerEquipment(payload);
            }

            this.handleRoundEnd(payload);
            // this.clearInterval();
            // this.clutchSchedule = setInterval(() => {this.handleRoundEnd(), this.clearInterval()}, this.MP_ROUND_RESTART_DELAY*1000);
            break;
          }
          case "half_voided": {
            // this.clearInterval();
            this.gameState.get(event.payload.mapNumber!)!.forEach((roundState, roundNumber) => {
              if (roundState.halfNumber === event.payload.halfNumber) {
                this.gameState.get(event.payload.mapNumber!)!.delete(roundNumber);
              }
            });
            break;
          }
          case "kill": {
            this.handleKill(event.payload as LiveEvents.RoundOccurrencePayload)
            break;
          }
          case "suicide": {
            this.handleSuicide(event.payload as LiveEvents.RoundOccurrencePayload);
            break;
          }
          case "assist": {
            this.handleAssist(event.payload as LiveEvents.RoundOccurrencePayload);

            break;
          };
          case "flash_assist": {
            this.handleFlashAssist(event.payload as LiveEvents.RoundOccurrencePayload);
            break;
          }
          case "bomb_planted": {
            this.handleBombPlanted(event.payload as LiveEvents.RoundOccurrencePayload);
            break;
          }
          case "equipment_state": { // Live 2.0 payload
            this.handlePlayerEquipment(payload);
            // this.clearInterval();
            // this.clutchSchedule = setInterval(() => {this.handleRoundEnd(), this.clearInterval()}, this.MP_ROUND_RESTART_DELAY*1000);
            break;
          }
          default: {
            break;
          }
        }
        this.updateFixture(this._fixture.maps[event.payload.mapNumber! - 1], this.gameState.get(event.payload.mapNumber!)!);
        break;
      }
      default: {
        break
      }
    }
    if (this._fixture.status.toLowerCase() === "started") {
      const defraggedFixture: FixtureDetailed = _.cloneDeep(this._fixture);
      defraggedFixture.maps = defraggedFixture.maps.filter((ele) => !!ele); // defrag map
      this.fixture.next(defraggedFixture);
    }
  }

  private getPlayerState(mapNumber: number, roundNumber: number, player: LiveEvents.Player): PlayerState {
    const roundState: RoundState = this.gameState.get(mapNumber)!.get(roundNumber)!;
    if (!roundState.teamStates.has(Number(player.teamId))) {
      roundState.teamStates.set(Number(player.teamId), { playerStates: new Map<number, PlayerState>(), potentialClutch: undefined })
    }

    let playerState: PlayerState | undefined = this.gameState.get(mapNumber)!.get(roundNumber)!.teamStates.get(Number(player.teamId))!.playerStates.get(Number(player.id));
    if (playerState) {
      return playerState;
    } else {
      playerState = { player: player, kills: 0, deaths: 0, alive: true, assists: 0, flashAssists: 0, entryKill: 0, solo: false, soloKills: 0, planter: false, clutchPlayer: false, clutches: [], multiKills: [], entryKillClutches: []};
    }

    roundState.teamStates.get(Number(player.teamId))!.playerStates.set(Number(player.id), playerState)
    this.checkSoloProps(mapNumber, roundNumber);
    return playerState;
  }

  private handleKill(payload: LiveEvents.RoundOccurrencePayload) {
    const roundState: RoundState = this.gameState.get(payload.mapNumber!)!.get(payload.roundNumber!)!;
    const killerState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber!, payload.killer!);
    const victimState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber!, payload.victim!);


    victimState.alive = false;
    victimState.deaths++;
    this.checkSoloProps(payload.mapNumber!, payload.roundNumber!);
    if (killerState.player.teamId !== victimState.player.teamId) {
      const playerStates: PlayerState[] = Array.from(roundState.teamStates, ([id, teamState]) => teamState)
        .flatMap(teamState => Array.from(teamState.playerStates, ([id, playerState]) => playerState));

      const entryKill: boolean = playerStates.filter(playerState => playerState.kills > 0).length === 0;
      if (entryKill) {
        killerState.entryKill++;
        killerState.entryKillClutches = [{ mapNumber: payload.mapNumber,  roundNumber:payload.roundNumber!}];
      }
      killerState.kills++;
      if (killerState.kills > 1) { // TODO might look weird on the live scoreboard
        killerState.multiKills = [{ id: killerState.kills!, mapNumber: payload.mapNumber,  roundNumber:payload.roundNumber!}];
      }
    }

  }

  private handleSuicide(payload: LiveEvents.RoundOccurrencePayload) {
    const playerState: PlayerState = this.getPlayerState(payload.mapNumber, payload.roundNumber, payload.player!);
    if (playerState.alive) { // TODO
      playerState.alive = false;
      playerState.deaths++;
      this.checkSoloProps(payload.mapNumber!, payload.roundNumber!)
    }
  }

  private handleAssist(payload: LiveEvents.RoundOccurrencePayload) {
    const playerState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber, payload.assister!);
    const victimState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber, payload.victim!);
    if (playerState.player.teamId !== victimState.player.teamId) {
      playerState!.assists++;
    }
  }

  private handleFlashAssist(payload: LiveEvents.RoundOccurrencePayload): void {
    const playerState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber, payload.assister!);
    const victimState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber, payload.victim!);
    if (playerState.player.teamId !== victimState.player.teamId) {
      playerState!.flashAssists++;
    }
  }

  private handleBombPlanted(payload: LiveEvents.RoundOccurrencePayload): void {
    const player: LiveEvents.Player = payload.planter || {id: payload.planterId!, name: payload.planterName!, side: "TERRORIST", teamId: payload.sourceTeamId!}; // Assumed terrorist
    const playerState: PlayerState = this.getPlayerState(payload.mapNumber!, payload.roundNumber, player);
    playerState.planter = true;
  }

  private handlePlayerEquipment(payload: LiveEvents.Payload): void {
    const players: LiveEvents.Player[] = (payload.playerEquipment || payload.players)!.map(playerEquipment => {
      return { id: playerEquipment.playerId, name: playerEquipment.name, side: undefined as unknown, teamId: playerEquipment.teamId } as LiveEvents.Player
    })
    players.forEach(player => this.getPlayerState(payload.mapNumber!, payload.roundNumber!, player));
    this.checkSoloProps(payload.mapNumber!, payload.roundNumber!)
  }

  private handleRoundEnd(payload?: LiveEvents.Payload): void {
    const roundState: RoundState = this.gameState.get(this.currentOccurrenceInfo.mapNumber!)!.get(this.currentOccurrenceInfo.roundNumber!)!;
    roundState.teamStates.forEach(teamState => {
      const planter: PlayerState = [...teamState.playerStates.entries()].map((player, index) => player[1]).filter(player => player.planter)[0];
      teamState.playerStates.forEach(playerState => {
      if (playerState.player.teamId === roundState.winnerId && (planter === playerState && planter.solo || playerState.clutchPlayer)) {
        playerState.clutches = [teamState.potentialClutch!];        
        if(!playerState.planter && payload?.winCondition === "bomb_exploded" && !planter?.solo) {
          playerState.clutches = [];
        }
      }
      if (playerState.kills > 1) {
        playerState.multiKills = [{ id: playerState.kills!, mapNumber: this.currentOccurrenceInfo.mapNumber!, roundNumber: this.currentOccurrenceInfo.roundNumber!}];
      }
    })});
    this.updateFixture(this._fixture.maps[this.currentOccurrenceInfo.mapNumber! - 1], this.gameState.get(this.currentOccurrenceInfo.mapNumber!)!);
  }

  /** Checks the roundstate for solo players and flags them */
  private checkSoloProps(mapNumber: number, roundNumber: number) {
    const teamStates = this.gameState.get(mapNumber)!.get(roundNumber)!.teamStates!;
    teamStates.forEach((team, id) => {
      const playerStates: PlayerState[] = Array.from(team.playerStates, ([id, playerState]) => playerState)
      const playersDead: PlayerState[] = playerStates.filter(player => !player.alive);
      if (playersDead.length === 4) {
        const lastPlayerAlive: PlayerState = playerStates.filter(player => player.alive)[0];
        if (lastPlayerAlive) {
          lastPlayerAlive.solo = true;
        }
        if (lastPlayerAlive && !lastPlayerAlive.clutches?.length && this.gameState.get(mapNumber)?.get(roundNumber)?.winnerId === undefined) {
          lastPlayerAlive.clutchPlayer = true;
        }
        if (!team.potentialClutch) {
          const allPlayerStates = Array.from(teamStates, ([teamId, teamState]) => Array.from(teamState.playerStates, ([playerId, playerState]) => playerState)).flat(1);
          const enemiesDead: number = allPlayerStates.filter(playerState => Number(playerState.player.teamId) !== id).filter(enemiesPlayers => !enemiesPlayers.alive).length;
          team.potentialClutch = { id: 5 - enemiesDead, mapNumber: mapNumber, roundNumber: roundNumber}; // assume that 5 players are playing
        }
      }
    })
  }

  private updateFixture(mapDetails: MapDetails, roundStates: Map<number, RoundState>) {

    // TODO refactor instead of checking for teamids

    const playerStates: PlayerState[] = Array.from(roundStates, ([id, roundState]) => roundState)
      .flat(1)
      .flatMap(roundState => Array.from(roundState.teamStates, ([id, teamState]) => teamState))
      .flatMap(teamState => Array.from(teamState.playerStates, ([id, playerState]) => playerState));

    const reducedPlayerState: PlayerState[] =
      _(playerStates)
        .groupBy(playerState => playerState.player.id)
        .map(states => ({
          player: states[0].player as LiveEvents.Player | undefined as unknown,
          kills: _.sumBy(states, player => player.kills!),
          deaths: _.sumBy(states, player => player.deaths!),
          assists: _.sumBy(states, player => player.assists!),
          flashAssists: _.sumBy(states, player => player.flashAssists!),
          entryKill: _.sumBy(states, player => player.entryKill!),
          alive: undefined as unknown,
          solo: undefined as unknown,
          clutches: _(states)
            .flatMap(playerState => playerState.clutches)
            .filter(clutch => clutch !== undefined)
            .value(),
          multiKills: _(states)
            .flatMap(playerState => playerState.multiKills)
            .filter(multiKill => multiKill !== undefined)
            .value(),
          entryKillClutches: _(states)
            .flatMap(playerState => playerState.entryKillClutches)
            .filter(multiKill => multiKill !== undefined)
            .value(),
        }))
        .value() as PlayerState[];

    const groupedReducedPlayerStates = _(reducedPlayerState).groupBy(playerState => playerState.player.teamId).value();
    mapDetails.teamStats = Object.keys(groupedReducedPlayerStates).map((key, i) => {
      const players: PlayerExpanded[] = groupedReducedPlayerStates[key].map(playerState => {
        return {
          adr: undefined as unknown,
          dpr: undefined as unknown,
          kpr: undefined as unknown,
          kast: undefined as unknown,
          name: playerState.player.name,
          kills: playerState.kills,
          deaths: playerState.deaths,
          assists: playerState.assists + playerState.flashAssists,
          playerId: Number(playerState.player.id),
          suicides: undefined as unknown,
          headshots: undefined as unknown,
          entryKills: playerState.entryKill,
          flash_assists: playerState.flashAssists,
          oneVersusXs: playerState.clutches,
          multiKills: playerState.multiKills,
          entryKillClutches: playerState.entryKillClutches
        } as PlayerExpanded
      })
      const teamStat: TeamStat = {
        kills: undefined as unknown,
        deaths: undefined as unknown,
        teamId: Number(key),
        assists: undefined as unknown,
        players: _(players).sortBy(player => player.kills).reverse().value(),
        suicides: undefined as unknown,
        headshots: undefined as unknown,
        startSide: undefined as unknown,
        entryKills: undefined as unknown,
        flash_assists: undefined as unknown
      } as TeamStat
      return teamStat;
    });
  }

  public getFixture(): Observable<FixtureDetailed> {
    return this.fixture
  }

  private clearInterval() {
    if(this.clutchSchedule) {
      clearInterval(this.clutchSchedule);
      this.clutchSchedule = undefined;
    }
  }
}